import { useEffect } from 'react'
import { useWorkspaceStore } from '@/stores/workspaceStore'

/**
 * Stale-scope self-heal for the two tenant headers apiFetch stamps.
 *
 * `apiFetch` dispatches `qyngent:workspace-reset` / `qyngent:org-reset` when a
 * response carries `X-Workspace-Reset` / `X-Org-Reset`, meaning the backend
 * could not honour our stored selection and served the account default
 * instead. It clears the persisted blob; this nulls the in-memory zustand
 * value so the dead header stops going out. AuthProvider re-resolves both
 * from `/api/auth/me` on the next load (it only initializes when the stored
 * value is empty).
 *
 * The two are handled independently: an unusable org selection must not throw
 * away a perfectly good workspace selection, or vice versa.
 *
 * The backend deliberately answers with a fallback + reset header rather than
 * a 403 — a 403 reaches `/api/auth/me/profile` and bounces the whole app to
 * onboarding (prod incident, 2026-09-05) — so this listener is the entire
 * convergence mechanism.
 */
export function useScopeReset(): void {
  useEffect(() => {
    const onWorkspaceReset = () => {
      useWorkspaceStore.getState().setActiveWorkspace(null)
    }
    const onOrgReset = () => {
      useWorkspaceStore.getState().setActiveOrg(null)
    }
    window.addEventListener('qyngent:workspace-reset', onWorkspaceReset)
    window.addEventListener('qyngent:org-reset', onOrgReset)
    return () => {
      window.removeEventListener('qyngent:workspace-reset', onWorkspaceReset)
      window.removeEventListener('qyngent:org-reset', onOrgReset)
    }
  }, [])
}
